import { defa } from 'defa-object';
import User     from 'scripts/defa/users/model-user';
import Service  from 'scripts/poppet/service';

/**
 * defa.services.UserHandler
 * A handler for user-related application events.
 * @version 1.2.0
 */


const channelUser = Bb.Radio.channel( 'user' );
const channel     = Bb.Radio.channel( 'objects' );

const UserHandler = Service.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	channelName: 'user',
	
	radioEvents: {
		'user:init' : 'initUser',
		'user:stale': 'refreshUser',
	},
	
	radioRequests: {
		'model'         : 'getUserModel',
		'units'         : 'getAllUnits',
		'customers'     : 'getCustomers',
		'id'            : 'getUserId',
		'is:logged:in'  : 'isLoggedIn',
		'is:logged:out' : 'isLoggedOut',
		'has:bluetooth' : 'hasBluetooth',
		'is:techsupport': 'isTechSupport',
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * @constructor
	 */
	initialize: function () {
		
		// this is the application's currentUser
		this.user = new User( {
			transientLoggedIn: false,
		} );
		
		this.listenTo( this.user, 'first', this.onUserFirst );
		this.listenTo( this.user, 'fresh', this.onUserRefresh );
		this.listenTo( this.user, 'change:transientHasBluetoothDevice', this.onUserChangeHasBluetooth );
		this.listenTo( this.user, 'change:transientLoggedIn', this.onUserChangeLoggedIn );
		
	},
	
	/**
	 * Trigges when the UserHandler has loaded a new fresh user.
	 * @void
	 */
	onUserRefresh: function () {
		channelUser.trigger( 'user:fresh' );
	},
	
	/**
	 * Triggers the first time the currentUser triggers either error or sync. (app start)
	 * @void
	 */
	onUserFirst: function () {
		channelUser.trigger( 'user:first' );
	},
	
	/**
	 * Triggers when the user changes it's hasBluetooth flag.
	 * @void
	 */
	onUserChangeHasBluetooth: function ( model, response ) {
		channelUser.trigger( 'user:change:bluetooth' );
	},
	
	/**
	 * Triggers when the user changes it's logged state.
	 * @void
	 */
	onUserChangeLoggedIn: function ( model, response ) {
		// trigger change event
		channelUser.trigger( 'user:change:logged', response );
		
		// also trigger a login or logout event
		if ( response ) {
			channelUser.trigger( 'user:logged:in' );
		} else {
			channelUser.trigger( 'user:logged:out' );
		}
	},
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Initalizes the current user from set defa.vars
	 * @void
	 */
	initUser: function () {
		this.user.fetchLoggedIn();  // rename to fetch from vars?
	},
	
	/**
	 * Retrieves a new version of the current user from the backend.
	 * @void
	 */
	refreshUser: function () {
		this.user.getMe();
	},
	
	/**
	 * Retrieves all units available for this user.
	 * @return {Collection}  A collection of units.
	 */
	getAllUnits: function () {
		let unitsUser      = channelUser.request( 'model' ).getUnits();
		let unitsBluetooth = channel.request( 'units' ).getBluetoothUnits();
		
		let units = new defa.collections.Units();
		units.add( unitsUser.models );
		units.add( unitsBluetooth.models );
		
		return units;
	},
	
	/**
	 * Retrieves the current user model.
	 * @return {user}  The current user model.
	 */
	getUserModel: function () {
		return this.user;
	},
	
	/**
	 * Retrieves the current user model's id.
	 * @return {int}  The current user model's id.
	 */
	getUserId: function () {
		return this.user.get( 'id' );
	},
	
	/**
	 * Gets the available customers for this user from it's channel.
	 * NOTE: Customer objects not in the channel will not be polled for.
	 * @return {collection}  A collection of this users available customers.
	 */
	getCustomers: function () {
		return this.user.get( 'customers' );
	},
	
	/**
	 * Checks if the user has bluetooth devices.
	 * @return {bool}  True if the user has any bluetooth devices, false if not.
	 */
	hasBluetooth: function () {
		return this.user.get( 'transientHasBluetoothDevice' );
	},
	
	/**
	 * Checks if the user is logged in.
	 * @return {bool}  True if the user is logged in, false if not.
	 */
	isLoggedIn: function () {
		return this.user.get( 'transientLoggedIn' );
	},
	
	/**
	 * Checks if the user is logged out.
	 * @return {bool} True if the user is logged out, false if not.
	 */
	isLoggedOut: function () {
		return !this.user.get( 'transientLoggedIn' );
	},
	
	/**
	 * Checks if the current user is a technical support member.
	 * @return {bool}  True if the current user is tech support, false if not.
	 */
	isTechSupport: function () {
		return this.user.isTechSupport();
	},
	
} );

export default UserHandler;